import React, { useState, useEffect } from 'react';
import { Box, Text, Image } from 'grommet';
import { FormNext } from 'grommet-icons';
import { analytics } from '../../services/analytics';
import SportsRecruitsAuthorizationModal from './SportsRecruitsAuthorizationModal';
import { shd } from '@shd/jslib/models';

interface SportsRecruitsBannerProps {
  player: shd.ClaimedPlayer;
  teams: shd.Team[] | null;
  isAdmin: boolean;
  isEntitledToView: boolean;
  claimed: boolean;
  openSRModal: boolean;
}

const SportsRecruitsBanner: React.FC<SportsRecruitsBannerProps> = ({
  player,
  teams,
  isAdmin,
  claimed,
  openSRModal,
  isEntitledToView = false,
}) => {
  const [showSRAuthorizationModal, setShowSRAuthorizationModal] =
    useState(false);
  const [isVbPlayer, setIsVbPlayer] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  /** @NOTE: Move this to `utils` once organized via #3017  */
  const isOnHighSchoolTeam = (ageLevel: string): boolean => {
    const validHighSchoolLevels: string[] = [
      '14U',
      '15U',
      '16U',
      '17U',
      '18U',
      'High School',
    ];
    return validHighSchoolLevels.includes(ageLevel);
  };

  useEffect(() => {
    if (player) {
      const isLinked =
        player.srAuthorizeUserId !== '' && player.srUserId !== '';
      const { yearGraduation, collegeCommitStatus } = player;
      let isRecruitable = false;

      if (collegeCommitStatus !== 10) {
        const futureYear = new Date().getFullYear() + 5;
        isRecruitable = !!yearGraduation && yearGraduation <= futureYear;
      }

      if (teams) {
        setIsVbPlayer(
          teams.some((team) => team.attrib_sportType === 'Volleyball')
        );
        for (const team of teams) {
          if (!isRecruitable && isOnHighSchoolTeam(team.attrib_ageLevel)) {
            isRecruitable = true;
            break;
          }
        }
      }

      const isEntitled = (): boolean => {
        if (isEntitledToView) {
          return true;
        }
        if (isVbPlayer) {
          return true;
        }
        return false;
      };

      const shouldShowBanner =
        isRecruitable && isAdmin && claimed && !isLinked && isEntitled();

      setShowBanner(shouldShowBanner);
      if (shouldShowBanner && openSRModal) {
        setShowSRAuthorizationModal(true);
      }
    }
  }, [
    player,
    teams,
    isAdmin,
    claimed,
    openSRModal,
    isEntitledToView,
    isVbPlayer,
  ]);

  const handleClick = () => {
    analytics.track('Link SportsRecruits Clicked');
    setShowSRAuthorizationModal(true);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <Box data-testid="sports-recruits-banner" margin="medium">
        <Box
          background="dark-5"
          direction="row"
          pad="medium"
          align="center"
          justify="between"
          round="small"
          onClick={handleClick}
        >
          <Box direction="row" align="center" gap="small">
            <Image src="/sr_logo.png" width="32px" />
            <Box gap="small">
              <Text weight="bold" size="large">
                Getting Recruited Starts Here
              </Text>
              <Text>
                Build or connect your FREE SportsRecruits profile today!
              </Text>
            </Box>
          </Box>
          <Box>
            <FormNext />
          </Box>
        </Box>
      </Box>
      {showSRAuthorizationModal && (
        <SportsRecruitsAuthorizationModal
          playerId={player._id}
          onClose={() => setShowSRAuthorizationModal(false)}
          fromDeepLink={openSRModal}
        />
      )}
    </>
  );
};

export default SportsRecruitsBanner;
